import { useEffect, useState } from 'react';
import { addDays, format, subDays } from 'date-fns';

import { useSettingsQuery } from '@features/setting/queries';
import type {
  BankSortBy,
  IntermediaryAnalyticsFilters,
  IntermediaryPaymentType,
} from '@models/intermediaryAnalytics';
const DEFAULT_MAX_DATE_RANGE = 30;

function toFilters(
  startDate: Date,
  endDate: Date,
  paymentType: IntermediaryPaymentType,
  minPaymentsThreshold: number,
): IntermediaryAnalyticsFilters {
  return {
    fromDate: format(startDate, 'yyyy-MM-dd'),
    toDate: format(endDate, 'yyyy-MM-dd'),
    paymentScheduleType: paymentType,
    minPaymentsThreshold: minPaymentsThreshold,
  };
}

export function useIntermediaryAnalyticsFilters() {
  const { data: settings } = useSettingsQuery();
  const minPaymentsThreshold =
    (settings?.find((s) => s.slug === 'intermediary.min_payments_threshold')?.setting
      ?.value as number) ?? 10;

  const maxDateRange =
    (settings?.find((s) => s.slug === 'intermediary.max_date_range')?.setting?.value as
      | number
      | undefined) ?? DEFAULT_MAX_DATE_RANGE;

  const today = new Date();
  const [filterByBrand, setFilterByBrand] = useState<string>('all');
  const [startDate, setStartDate] = useState<Date>(() => subDays(today, 30));
  const [endDate, setEndDate] = useState<Date>(() => today);
  const [paymentType, setPaymentType] = useState<IntermediaryPaymentType>('all');
  const [sortBy, setSortBy] = useState<BankSortBy>('totalPaymentCount');
  const [submittedFilters, setSubmittedFilters] = useState<IntermediaryAnalyticsFilters>(() =>
    toFilters(subDays(today, 30), today, 'all', minPaymentsThreshold),
  );

  useEffect(() => {
    setSubmittedFilters((prev) => ({
      ...prev,
      minPaymentsThreshold,
    }));
  }, [minPaymentsThreshold]);

  const maxAllowedEnd = addDays(startDate, maxDateRange);
  const endDateMax = maxAllowedEnd > today ? today : maxAllowedEnd;
  const currentFilters = toFilters(startDate, endDate, paymentType, minPaymentsThreshold);
  const hasDateError = startDate > today || endDate < startDate || endDate > endDateMax;
  const isDirty =
    currentFilters.fromDate !== submittedFilters.fromDate ||
    currentFilters.toDate !== submittedFilters.toDate ||
    currentFilters.paymentScheduleType !== submittedFilters.paymentScheduleType;

  const processorConfig = settings?.find((s) => s.slug === 'processor_merchant_id');
  const brandCodes =
    typeof processorConfig === 'object'
      ? Object.values(processorConfig.setting.value)
          .map((item) => (item as { brandCode?: string }).brandCode)
          .filter(Boolean)
      : [];
  return {
    brandCodes: [...new Set(brandCodes)] as string[],
    processorConfig: processorConfig,
    settings: settings ?? [],
    today,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    endDateMax,
    paymentType,
    setPaymentType,
    sortBy,
    setSortBy,
    submittedFilters,
    hasDateError,
    isDirty,
    filterByBrand,
    setFilterByBrand,
    maxDateRange,
    minPaymentsThreshold,
    handleSubmit: () => setSubmittedFilters(currentFilters),
    areFiltersReadyForQuery:
      settings !== undefined && submittedFilters.minPaymentsThreshold === minPaymentsThreshold,
  };
}
